import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Trophy, BarChart3, Settings, LogOut, 
  Play, Clock, Star, ChevronRight, User, Crown, Target, TrendingUp, Award
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsByGame, setStatsByGame] = useState(null);
  const [recentGames, setRecentGames] = useState([]);
  const [achievementSummary, setAchievementSummary] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    
    const fetchStatsByGame = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/by-game`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStatsByGame(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats by game:', error);
      }
    };

    const fetchRecentGames = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/history?limit=5`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setRecentGames(data);
        }
      } catch (error) {
        console.error('Failed to fetch recent games:', error);
      }
    };

    const fetchAchievementSummary = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/achievements/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAchievementSummary(data);
        }
      } catch (error) {
        console.error('Failed to fetch achievement summary:', error);
      }
    };

    if (isAuthenticated) {
      fetchStats();
      fetchStatsByGame();
      fetchRecentGames();
      fetchAchievementSummary();
    }
  }, [isAuthenticated, token]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  const getGameIcon = (gameType) => {
    switch (gameType) {
      case 'chess': return '♟️';
      case 'checkers': return '⚫';
      case 'triangle_checkers': return '🔺';
      case 'baseball': return '⚾';
      case 'baseball_derby': return '⚾';
      case 'bridge': return '🃏';
      default: return '🎮';
    }
  };
  
  const getGameName = (gameType) => {
    switch (gameType) {
      case 'chess': return 'Chess';
      case 'checkers': return 'Checkers';
      case 'triangle_checkers': return 'Triangle';
      case 'baseball': return 'Baseball';
      case 'baseball_derby': return 'Derby';
      case 'bridge': return 'Bridge';
      default: return gameType;
    }
  };

  const games = [
    {
      id: 'chess',
      name: 'Chess',
      icon: '♟️',
      description: 'Stockfish AI & Coach Mode',
      status: 'available',
      path: '/games/chess',
      accent: 'blue'
    },
    {
      id: 'checkers',
      name: 'Checkers',
      icon: '⚫',
      description: 'American & International',
      status: 'available',
      path: '/games/checkers',
      accent: 'orange'
    },
    {
      id: 'triangle_checkers',
      name: 'Triangle',
      icon: '🔺',
      description: 'Hexagonal racing game',
      status: 'available',
      path: '/games/triangle-checkers',
      accent: 'purple'
    },
    {
      id: 'baseball',
      name: 'Baseball',
      icon: '⚾',
      description: 'Derby & Multiplayer Duels',
      status: 'available',
      path: '/games/baseball',
      accent: 'amber'
    },
    {
      id: 'bridge',
      name: 'Bridge',
      icon: '🃏',
      description: 'Classic trick-taking game',
      status: 'available',
      path: '/games/bridge',
      accent: 'rose'
    },
    {
      id: 'three_card_stud',
      name: 'Poker',
      icon: '🎰',
      description: 'Hold\'em & Three Card',
      status: 'available',
      path: '/games/poker',
      accent: 'emerald'
    },
    {
      id: 'solitaire',
      name: 'Solitaire',
      icon: '🃁',
      description: 'Classic Klondike',
      status: 'available',
      path: '/games/solitaire',
      accent: 'cyan'
    },
    {
      id: 'teen_patti',
      name: 'Teen Patti',
      icon: '🎴',
      description: 'Three Card Poker',
      status: 'available',
      path: '/games/teen-patti',
      accent: 'amber'
    }
  ];

  const accentStyles = {
    blue: { border: 'border-blue-500/20', hover: 'hover:border-blue-500/40', bg: 'bg-blue-500/10', text: 'text-blue-400' },
    orange: { border: 'border-orange-500/20', hover: 'hover:border-orange-500/40', bg: 'bg-orange-500/10', text: 'text-orange-400' },
    purple: { border: 'border-purple-500/20', hover: 'hover:border-purple-500/40', bg: 'bg-purple-500/10', text: 'text-purple-400' },
    amber: { border: 'border-amber-500/20', hover: 'hover:border-amber-500/40', bg: 'bg-amber-500/10', text: 'text-amber-400' },
    rose: { border: 'border-rose-500/20', hover: 'hover:border-rose-500/40', bg: 'bg-rose-500/10', text: 'text-rose-400' },
    emerald: { border: 'border-emerald-500/20', hover: 'hover:border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
    cyan: { border: 'border-cyan-500/20', hover: 'hover:border-cyan-500/40', bg: 'bg-cyan-500/10', text: 'text-cyan-400' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0a1a] via-[#1a1025] to-[#0f0a1a]">
      {/* Cosmic background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/10 via-transparent to-transparent" />
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0f0a1a]/90 backdrop-blur-md border-b border-purple-900/30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
            data-testid="nav-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_4e3de3c9-461d-4f73-ada0-30b07137d950/artifacts/0wd7buyq_Boosty%20Games%20v2.png" 
              alt="Boosty Games" 
              className="h-12 w-auto"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-900/30 border border-purple-800/50">
              <User className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-slate-300 hidden sm:inline">{user?.name || 'Player'}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-purple-900/30"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 relative z-10">
        {/* Welcome + Stats Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-slate-500 text-sm font-mono uppercase tracking-wider mb-1">Dashboard</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">
              Welcome back, {user?.name?.split(' ')[0] || 'Player'}
            </h1>
          </motion.div>
          
          {/* Quick Stats Bar */}
          {stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex gap-6"
            >
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-white">{stats.total_games || 0}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Games</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-emerald-400">{stats.win_rate || 0}%</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Win Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-amber-400">{stats.current_streak || 0}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">Streak</div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Games Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-400" />
                Games
              </h2>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {games.map((game, index) => {
                const styles = accentStyles[game.accent];
                return (
                  <motion.div
                    key={game.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                    onClick={() => game.status === 'available' && navigate(game.path)}
                    className={`
                      relative p-4 sm:p-5 rounded-xl border ${styles.border} ${styles.hover}
                      bg-obsidian-card cursor-pointer transition-all duration-200
                      hover:scale-[1.02] active:scale-[0.98]
                      touch-manipulation select-none
                    `}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    data-testid={`game-card-${game.id}`}
                  >
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <span className="text-2xl sm:text-3xl">{game.icon}</span>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        <span className="text-xs text-slate-500">Live</span>
                      </div>
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-white mb-0.5 sm:mb-1">{game.name}</h3>
                    <p className="text-xs text-slate-500 mb-3 sm:mb-4 line-clamp-1 sm:line-clamp-none">{game.description}</p>
                    <Button
                      size="sm"
                      className={`w-full ${styles.bg} ${styles.text} hover:opacity-80 border-0 font-medium h-8 sm:h-9 text-xs sm:text-sm`}
                    >
                      <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5" />
                      Play
                    </Button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="lg:col-span-4 space-y-4"
          >
            {/* Stats by Game */}
            {statsByGame && Object.keys(statsByGame).filter(k => k !== 'total' && statsByGame[k]).length > 0 && (
              <div className="p-4 rounded-xl border border-slate-800 bg-obsidian-card">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-400" />
                  Stats by Game
                </h3>
                <div className="space-y-3">
                  {['chess', 'checkers', 'triangle_checkers'].map((gameType) => {
                    const gameStats = statsByGame[gameType];
                    if (!gameStats || !gameStats.total_games) return null;
                    
                    return (
                      <div key={gameType} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getGameIcon(gameType)}</span>
                          <span className="text-sm text-slate-300">{getGameName(gameType)}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-slate-500">{gameStats.total_games}g</span>
                          <span className="text-emerald-400 font-mono">{gameStats.win_rate}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Games */}
            {recentGames.length > 0 && (
              <div className="p-4 rounded-xl border border-slate-800 bg-obsidian-card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    Recent
                  </h3>
                  <button 
                    onClick={() => navigate('/profile')}
                    className="text-xs text-slate-500 hover:text-purple-400 transition-colors"
                  >
                    View All
                  </button>
                </div>
                <div className="space-y-2">
                  {recentGames.slice(0, 4).map((game, index) => (
                    <div
                      key={game.id || index}
                      className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getGameIcon(game.game_type)}</span>
                        <div>
                          <div className="text-sm text-slate-300">{getGameName(game.game_type)}</div>
                          <div className="text-xs text-slate-600">
                            {game.played_at ? new Date(game.played_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                      <div className={`
                        px-2 py-0.5 rounded text-xs font-medium
                        ${game.result === 'win' 
                          ? 'bg-emerald-500/10 text-emerald-400' 
                          : game.result === 'loss'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-slate-500/10 text-slate-400'
                        }
                      `}>
                        {game.result === 'win' ? 'W' : game.result === 'loss' ? 'L' : 'D'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Achievements Widget */}
            {isAuthenticated && achievementSummary && (
              <div 
                onClick={() => navigate('/achievements')}
                className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 cursor-pointer hover:bg-amber-500/10 transition-all"
                data-testid="achievements-widget"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-400" />
                    Achievements
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-amber-400">{achievementSummary.unlocked_count}</span>
                      <span className="text-sm text-slate-500">/ {achievementSummary.total_count}</span>
                    </div>
                    <div className="text-xs text-slate-500">Unlocked</div>
                  </div>
                  <div className="flex-1 text-right">
                    <div className="text-2xl font-bold text-purple-400">{achievementSummary.total_points}</div>
                    <div className="text-xs text-slate-500">Points</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-purple-500 rounded-full transition-all"
                    style={{ width: `${(achievementSummary.unlocked_count / achievementSummary.total_count) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => navigate('/profile')}
                className="p-3 rounded-xl border border-slate-800 bg-obsidian-card hover:border-slate-700 transition-colors flex flex-col items-center gap-1.5"
                data-testid="quick-action-profile"
              >
                <User className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-500">Profile</span>
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="p-3 rounded-xl border border-slate-800 bg-obsidian-card hover:border-slate-700 transition-colors flex flex-col items-center gap-1.5"
                data-testid="quick-action-settings"
              >
                <Settings className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-500">Settings</span>
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
